import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@atlas/ui-components'
import { Button, Checkbox, Combobox, Popover, PopoverContent } from '@atlas/ui-primitives'
import { Plus } from 'lucide-react'
import type { MatrixEditController } from './MatrixRowView'
import { MatrixRowView } from './MatrixRowView'
import type { MatrixRow } from './matrix-model'
import { BulkSelectState, bulkSelectState, selectableKeysInColumn, tickableRows } from './matrix-model'

/**
 * Which write affordances the grid offers. Each one is set independently by the
 * surface embedding the grid, and none is derived from another: the Messages page
 * grants all three (it carries no per-user gating of its own), while a Campaign
 * passes its own resolved capabilities through.
 */
export interface MatrixWriteCapabilities {
	/** Creating a new piece of copy from the grid. */
	canAddMessage: boolean
	/** Queueing AI translations - and with it every tick box, which exists only to feed that action. */
	canTranslate: boolean
	/** Editing a row's copy in place. */
	canEditRows: boolean
}

/**
 * Both grow-the-grid pluses wear this: a dashed ghost the width of its own
 * label, reading as "the grid continues here" rather than as an action of its
 * own. `onPress` is left off when a popover owns the press.
 */
export function GrowGridButton({ label, onPress }: { label: string; onPress?: () => void }) {
	return (
		<Button
			variant="secondary"
			size="sm"
			// `whitespace-nowrap`: the trailing column is the one the table shrinks
			// first, and a two-line label there outgrows the header row it sits in.
			className="w-fit gap-1.5 whitespace-nowrap rounded-ds-md border border-ds-border border-dashed bg-transparent px-3.5 font-normal text-ds-text-muted"
			onPress={onPress}
		>
			<Plus className="h-3 w-3" aria-hidden="true" />
			{label}
		</Button>
	)
}

/**
 * The trailing head cell: the grow-a-column plus, sitting past the last locale.
 * Its picker lives in a popover, left open after a pick - the option just chosen
 * leaves the list, so consecutive columns can be added without reopening it.
 *
 * The cell's own vertical padding is what keeps the button off the header's rule:
 * a head cell is exactly as tall as the button, so flush is the default.
 */
function AddLocaleColumnHead({
	availableLocaleOptions,
	onAddLocale,
}: {
	availableLocaleOptions: { value: string; label: string }[]
	onAddLocale: (locale: string) => void
}) {
	return (
		<TableHead className="h-8 px-2 py-1.5 align-middle">
			<Popover>
				<GrowGridButton label="Add locale" />
				<PopoverContent className="p-3">
					<Combobox
						aria-label="Locale to add"
						options={availableLocaleOptions}
						// Uncontrolled by design: choosing a locale adds a column rather than
						// leaving a selection behind, so the picker returns to its prompt.
						value={null}
						onChange={(v) => v && onAddLocale(v)}
						placeholder="Search locales…"
						className="w-64"
					/>
				</PopoverContent>
			</Popover>
		</TableHead>
	)
}

/** The grow-a-row plus as the grid's last row: the cell spans every column, the button stays the size of its label. */
function AddMessageRow({ colSpan, onOpen }: { colSpan: number; onOpen: () => void }) {
	return (
		<TableRow className="hover:bg-transparent">
			<TableCell colSpan={colSpan} className="p-2">
				<GrowGridButton label="Add message" onPress={onOpen} />
			</TableCell>
		</TableRow>
	)
}

/** A locale column's heading and its whole-column tick, dropped when no row in the column can be translated. */
function LocaleColumnHead({
	locale,
	rows,
	selection,
	canSelect,
	onToggleLocale,
}: {
	locale: string
	rows: readonly MatrixRow[]
	selection: ReadonlySet<string>
	canSelect: boolean
	onToggleLocale: (locale: string) => void
}) {
	const keys = selectableKeysInColumn(rows, locale)
	const state = bulkSelectState(keys, selection)
	// A header's name is announced with every cell under it, so it stays the locale
	// alone; the tick inside keeps its own label for its own sake.
	return (
		<TableHead aria-label={locale} className="h-8 px-2 font-mono text-ds-2xs uppercase tracking-wide">
			<span className="flex items-center gap-2">
				{canSelect && keys.length > 0 ? (
					<Checkbox
						size="sm"
						isSelected={state === BulkSelectState.ALL}
						isIndeterminate={state === BulkSelectState.SOME}
						onChange={() => onToggleLocale(locale)}
						aria-label={`Select every message for ${locale}`}
					/>
				) : null}
				{locale}
			</span>
		</TableHead>
	)
}

/** The grid proper: the locale headings, one row per translation group, and the grow-a-row plus under them. */
export function MatrixGrid({
	rows,
	localeColumns,
	selection,
	capabilities,
	hasAddLocaleColumn,
	availableLocaleOptions,
	onAddLocale,
	onToggleCell,
	onToggleRow,
	onToggleLocale,
	edit,
	onOpenAddMessage,
}: {
	rows: MatrixRow[]
	localeColumns: string[]
	selection: ReadonlySet<string>
	capabilities: MatrixWriteCapabilities
	hasAddLocaleColumn: boolean
	availableLocaleOptions: { value: string; label: string }[]
	onAddLocale: (locale: string) => void
	onToggleCell: (sourceMessageId: string, locale: string) => void
	onToggleRow: (row: MatrixRow) => void
	onToggleLocale: (locale: string) => void
	edit: MatrixEditController
	/** Null when this surface cannot add a message, or the draft is already open. */
	onOpenAddMessage: (() => void) | null
}) {
	// The add-row plus spans the whole grid, trailing plus column included.
	const gridColumnCount = 1 + localeColumns.length + (hasAddLocaleColumn ? 1 : 0)
	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead className="h-8 px-2 font-mono text-ds-2xs uppercase tracking-wide">Message</TableHead>
					{localeColumns.map((locale) => (
						<LocaleColumnHead
							key={locale}
							locale={locale}
							rows={tickableRows(rows, edit.editingRowId)}
							selection={selection}
							canSelect={capabilities.canTranslate}
							onToggleLocale={onToggleLocale}
						/>
					))}
					{hasAddLocaleColumn ? (
						<AddLocaleColumnHead availableLocaleOptions={availableLocaleOptions} onAddLocale={onAddLocale} />
					) : null}
				</TableRow>
			</TableHeader>
			<TableBody>
				{rows.map((row) => (
					<MatrixRowView
						key={row.sourceMessageId}
						row={row}
						selection={selection}
						canSelect={capabilities.canTranslate}
						canEdit={capabilities.canEditRows}
						onToggleCell={onToggleCell}
						onToggleRow={onToggleRow}
						edit={edit}
						hasTrailingColumn={hasAddLocaleColumn}
					/>
				))}
				{onOpenAddMessage ? <AddMessageRow colSpan={gridColumnCount} onOpen={onOpenAddMessage} /> : null}
			</TableBody>
		</Table>
	)
}
